import React, { Suspense, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useWhoami } from '@cotera/client/app/stores/org';
import {
  ModalPageView,
  useModalManager,
} from '@cotera/client/app/components/app/modal-manager/modal-manager';
import type { ModalItem } from '@cotera/client/app/components/app/modal-manager/modal-manager';
import { NewTriggerWizard } from './new-trigger-wizard';

// Right-sized for a focused wizard, matching the add-node modal so the two
// flows feel like one family: a comfortable reading column up to ~85vh.
const NEW_TRIGGER_MODAL_SIZE = {
  custom: 'w-[92vw] max-w-2xl max-h-[85dvh] rounded-lg',
} as const;

export type OpenNewTriggerModalOptions = {
  folderId?: string;
  /** Runs after a successful create (the pushed modal is already popped). */
  onCreated?: (result: { workflowId: string }) => void;
};

type ModalManagerActions = {
  push: (item: ModalItem) => void;
  pop: () => void;
};

/**
 * Pushes the stepped "new trigger" modal. Extracted so both the React hook and
 * the command-palette action (which has no hook access) open the same modal.
 */
export const pushNewTriggerModal = (
  modalManager: ModalManagerActions,
  options: OpenNewTriggerModalOptions & {
    onCreated: (result: { workflowId: string }) => void;
  }
): void => {
  modalManager.push({
    size: NEW_TRIGGER_MODAL_SIZE,
    view: () => (
      <ModalPageView className="flex-1 flex-col px-5 py-6 sm:px-10 sm:py-8">
        <Suspense
          fallback={
            <div className="py-16 text-center text-sm text-muted-foreground">
              Loading…
            </div>
          }
        >
          <NewTriggerWizard
            folderId={options.folderId}
            onCreated={(result) => {
              modalManager.pop();
              options.onCreated(result);
            }}
            onCancel={() => modalManager.pop()}
          />
        </Suspense>
      </ModalPageView>
    ),
  });
};

/**
 * Opens the stepped "new trigger" modal. Without an `onCreated` override it
 * opens the freshly-created workflow in the graph editor, where the new trigger
 * node sits ready to wire up.
 */
export const useOpenNewTriggerModal = (): ((
  options?: OpenNewTriggerModalOptions
) => void) => {
  const modalManager = useModalManager((s) => s.actions);
  const navigate = useNavigate();
  const org = useWhoami((s) => s.org);

  return useCallback(
    (options?: OpenNewTriggerModalOptions) => {
      pushNewTriggerModal(modalManager, {
        folderId: options?.folderId,
        onCreated:
          options?.onCreated ??
          ((result) => navigate(`/${org.slug}/workflows/${result.workflowId}`)),
      });
    },
    [modalManager, navigate, org.slug]
  );
};
